"use client"

import { motion } from "framer-motion";
import { useData } from 'vike-react/useData';

import Image from '@/components/Image';

import type { Data } from './+data';

function FloatingPaths({ position }: { position: number }) {
  const paths = Array.from({ length: 36 }, (_, i) => ({
    id: i,
    d: `M-${380 - i * 5 * position} -${189 + i * 6}C-${
      380 - i * 5 * position
    } -${189 + i * 6} -${312 - i * 5 * position} ${216 - i * 6} ${
      152 - i * 5 * position
    } ${343 - i * 6}C${616 - i * 5 * position} ${470 - i * 6} ${
      684 - i * 5 * position
    } ${875 - i * 6} ${684 - i * 5 * position} ${875 - i * 6}`,
    color: `rgba(15,23,42,${0.1 + i * 0.03})`,
    width: 0.5 + i * 0.03,
  }))

  return (
    <div className="absolute inset-0 pointer-events-none">
      <svg className="w-full h-full text-cyan-500/20" viewBox="0 0 696 316" fill="none" aria-hidden="true">
        {paths.map((path) => (
          <motion.path
            key={path.id}
            d={path.d}
            stroke="currentColor"
            strokeWidth={path.width}
            strokeOpacity={0.1 + path.id * 0.03}
            initial={{ pathLength: 0.3, opacity: 0.6 }}
            animate={{
              pathLength: 1,
              opacity: [0.3, 0.6, 0.3],
              pathOffset: [0, 1, 0],
            }}
            transition={{
              duration: 20 + Math.random() * 10,
              repeat: Number.POSITIVE_INFINITY,
              ease: "linear",
            }}
          />
        ))}
      </svg>
    </div>
  )
}

export default function Page() {
  const {
    p4RQiX,
    D9v7FJ,
    j90123,
    w2eMVa,
    OYoojt,
    wa37oN,
    k3aa56,
  } = useData<Data>();

  if (!p4RQiX) {
    return null;
  }

  return (
    <div className="h-full flex flex-col">
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-white via-cyan-50 to-cyan-100">
          <div className="absolute inset-0 opacity-30">
            <svg className="w-full h-full" viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <defs>
                <filter id="caustics">
                  <feTurbulence baseFrequency="0.02" numOctaves="3" result="noise" />
                  <feDisplacementMap in="SourceGraphic" in2="noise" scale="20" />
                </filter>
                <radialGradient id="water1" cx="30%" cy="40%">
                  <stop offset="0%" stopColor="rgba(6, 182, 212, 0.4)" />
                  <stop offset="100%" stopColor="rgba(6, 182, 212, 0.1)" />
                </radialGradient>
                <radialGradient id="water2" cx="70%" cy="60%">
                  <stop offset="0%" stopColor="rgba(34, 211, 238, 0.3)" />
                  <stop offset="100%" stopColor="rgba(34, 211, 238, 0.05)" />
                </radialGradient>
              </defs>
              <circle cx="300" cy="400" r="200" fill="url(#water1)" filter="url(#caustics)" className="animate-pulse" />
              <circle
                cx="700"
                cy="600"
                r="150"
                fill="url(#water2)"
                filter="url(#caustics)"
                className="animate-pulse"
                style={{ animationDelay: "1s" }}
              />
              <ellipse
                cx="500"
                cy="200"
                rx="100"
                ry="80"
                fill="rgba(6, 182, 212, 0.2)"
                filter="url(#caustics)"
                className="animate-pulse"
                style={{ animationDelay: "2s" }}
              />
            </svg>
          </div>
        </div>
      </div>
      <div className="absolute inset-0">
        <FloatingPaths position={1} />
        <FloatingPaths position={-1} />
      </div>
      <header className="relative z-10 bg-black h-[300px] flex items-center justify-center">
        <h1 className="text-white text-6xl md:text-7xl lg:text-8xl font-bold tracking-tight">
          <Image src="/images/logo-holi-white.png" alt="holi.agency" width="300px" />
        </h1>
      </header>
      <main className="relative z-10 px-6 py-16 mb-20">
        <div className="max-w-4xl mx-auto">
          {/* Liquid glass container */}
          <div className="backdrop-blur-xl bg-white/20 border border-white/30 p-12 md:p-24 shadow-2xl shadow-cyan-500/20">
            <div className="space-y-6 text-gray-800">
              <p className="text-2xl leading-relaxed pb-6">
                {p4RQiX} <span className="whitespace-nowrap">{D9v7FJ}</span><br/>{j90123} <span className="whitespace-nowrap">{w2eMVa}</span> <span className="whitespace-nowrap">{OYoojt}</span>
              </p>
              <p className="text-2xl leading-relaxed">
                {wa37oN} <a href="mailto:info@holi.agency" className="font-bold whitespace-nowrap inline-block hover:bg-gradient-to-r hover:from-base-content hover:via-cyan-500 hover:to-base-content hover:bg-[length:200%_100%] hover:bg-clip-text hover:text-transparent animate-sheen group">info<span><svg width="24" height="24" viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="inline stroke-black group-hover:stroke-cyan-500" aria-hidden="true"><circle cx="12" cy="12" r="4"/><path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-4 8"/></svg></span>holi.agency</a>
              </p>
            </div>
          </div>
        </div>
      </main>
      <div className="wabtn fixed bottom-0 right-0 md:bottom-4 md:right-4 lg:bottom-8 lg:right-8" id="wabutton">
        <a wa-tooltip={k3aa56} target="_blank" rel="noreferrer" href="https://wa.me/50660025531?text=Hi!%20I'm%20interested%20in%20your%20services%20and%20would%20love%20to%20know%20more%20about%20it.%20Could%20you%20please%20send%20me%20more%20information?%20Thank%20you!" style={{cursor: "pointer", height: "48px",width: "48px",padding: "7px 7px 7px 7px",position: "fixed",color: "#fff",bottom: "20px",right: "20px",display: "flex",textDecoration: "none",fontSize: "18px",fontWeight: 600,fontFamily: "sans-serif",alignItems: "center",zIndex: "999999999 !important",backgroundColor: "#00E785",boxShadow: "4px 5px 10px rgba(0, 255, 255, 0.4)",borderRadius: "100px",animation: "pulse 2.5s ease infinite",}}>
          <svg width="34" height="34" style={{padding: '5px'}} viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><g clipPath="url(#clip0_1024_354)"><path d="M23.8759 4.06939C21.4959 1.68839 18.3316 0.253548 14.9723 0.0320463C11.613 -0.189455 8.28774 0.817483 5.61565 2.86535C2.94357 4.91323 1.10682 7.86244 0.447451 11.1638C-0.21192 14.4652 0.351026 17.8937 2.03146 20.8109L0.0625 28.0004L7.42006 26.0712C9.45505 27.1794 11.7353 27.7601 14.0524 27.7602H14.0583C16.8029 27.7599 19.4859 26.946 21.768 25.4212C24.0502 23.8965 25.829 21.7294 26.8798 19.1939C27.9305 16.6583 28.206 13.8682 27.6713 11.1761C27.1367 8.48406 25.8159 6.01095 23.8759 4.06939ZM14.0583 25.4169H14.0538C11.988 25.417 9.96008 24.8617 8.1825 23.8091L7.7611 23.5593L3.3945 24.704L4.56014 20.448L4.28546 20.0117C2.92594 17.8454 2.32491 15.2886 2.57684 12.7434C2.82877 10.1982 3.91938 7.80894 5.67722 5.95113C7.43506 4.09332 9.76045 2.87235 12.2878 2.48017C14.8152 2.08799 17.4013 2.54684 19.6395 3.78457C21.8776 5.02231 23.641 6.96875 24.6524 9.3179C25.6638 11.6671 25.8659 14.2857 25.2268 16.7622C24.5877 19.2387 23.1438 21.4326 21.122 22.999C19.1001 24.5655 16.6151 25.4156 14.0575 25.4157L14.0583 25.4169Z" fill="#E0E0E0"/><path fillRule="evenodd" clipRule="evenodd" d="M10.6291 7.98363C10.3723 7.41271 10.1019 7.40123 9.85771 7.39143C9.65779 7.38275 9.42903 7.38331 9.20083 7.38331C9.0271 7.3879 8.8562 7.42837 8.69887 7.5022C8.54154 7.57602 8.40119 7.68159 8.28663 7.81227C7.899 8.17929 7.59209 8.62305 7.38547 9.11526C7.17884 9.60747 7.07704 10.1373 7.08655 10.6711C7.08655 12.3578 8.31519 13.9877 8.48655 14.2164C8.65791 14.4452 10.8581 18.0169 14.3425 19.3908C17.2382 20.5327 17.8276 20.3056 18.4562 20.2485C19.0848 20.1913 20.4843 19.4194 20.7701 18.6189C21.056 17.8183 21.0557 17.1323 20.9701 16.989C20.8844 16.8456 20.6559 16.7605 20.3129 16.5889C19.9699 16.4172 18.2849 15.5879 17.9704 15.4736C17.656 15.3594 17.4275 15.3023 17.199 15.6455C16.9705 15.9888 16.3139 16.7602 16.1137 16.9895C15.9135 17.2189 15.7136 17.2471 15.3709 17.0758C14.3603 16.6729 13.4275 16.0972 12.6143 15.3745C11.8648 14.6818 11.2221 13.8819 10.7072 13.0007C10.5073 12.6579 10.6857 12.472 10.8579 12.3007C11.0119 12.1472 11.2006 11.9005 11.3722 11.7003C11.5129 11.5271 11.6282 11.3346 11.7147 11.1289C11.7603 11.0343 11.7817 10.9299 11.7768 10.825C11.7719 10.7201 11.7409 10.6182 11.6867 10.5283C11.6001 10.3566 10.9337 8.66151 10.6291 7.98363Z" fill="white"/><path d="M23.7628 4.02445C21.4107 1.66917 18.2825 0.249336 14.9611 0.0294866C11.6397 -0.190363 8.35161 0.804769 5.70953 2.82947C3.06745 4.85417 1.25154 7.77034 0.600156 11.0346C-0.051233 14.299 0.506321 17.6888 2.16894 20.5724L0.222656 27.6808L7.49566 25.7737C9.50727 26.8692 11.7613 27.4432 14.0519 27.4434H14.0577C16.7711 27.4436 19.4235 26.6392 21.6798 25.1321C23.936 23.6249 25.6947 21.4825 26.7335 18.9759C27.7722 16.4693 28.0444 13.711 27.5157 11.0497C26.9869 8.38835 25.6809 5.94358 23.7628 4.02445ZM14.0577 25.1269H14.0547C12.0125 25.1271 10.0078 24.5782 8.25054 23.5377L7.8339 23.2907L3.51686 24.4222L4.66906 20.2143L4.39774 19.7831C3.05387 17.6415 2.4598 15.1141 2.70892 12.598C2.95804 10.082 4.03622 7.72013 5.77398 5.88366C7.51173 4.04719 9.81051 2.84028 12.3089 2.45266C14.8074 2.06505 17.3638 2.5187 19.5763 3.74232C21.7888 4.96593 23.5319 6.89011 24.5317 9.21238C25.5314 11.5346 25.7311 14.1233 25.0993 16.5714C24.4675 19.0195 23.0401 21.1883 21.0414 22.7367C19.0427 24.2851 16.5861 25.1254 14.0577 25.1255V25.1269Z" fill="white"/></g><defs><clipPath id="clip0_1024_354"><rect width="27.8748" height="28" fill="white" transform="translate(0.0625)"/></clipPath></defs></svg>
          <span className="button-text"/>
        </a>
      </div>
    </div>
  );
}
